import { useMemo, useState } from "react";
import { CheckCircle2, Loader2, ChevronDown } from "lucide-react";

/**
 * BucketWearGrid — mobile-app-style wear picker for a bucket section.
 *
 * Replaces the verbose one-row-per-position TaskCard rendering with a
 * compact position grid grouped by category (Tooth / Lip Shroud / Wing
 * Shroud). Each tile shows the position's current wear as a coloured pill;
 * tapping the tile exposes the NEW / 25% / 50% / 75% picker inline.
 *
 * A "Set all" quick-strip lets a supervisor bulk-apply a value to every
 * position in a category in a single tap.
 */
export default function BucketWearGrid({
  items,               // wear_pct items from the section
  itemsState,          // { [itemId]: { status, clicked_by, clicked_at, ... } }
  savingItems,         // Set<string>
  disabled,
  onStatus,            // (item, statusToken) — writes a single position
  onApplyToAll,        // (categoryItems, statusToken) — bulk-apply
  testid = "bucket-wear-grid",
}) {
  const groups = useMemo(() => groupByCategory(items), [items]);
  if (groups.length === 0) return null;
  return (
    <div className="space-y-4" data-testid={testid}>
      {groups.map((g) => (
        <WearGroup
          key={g.category}
          group={g}
          itemsState={itemsState}
          savingItems={savingItems}
          disabled={disabled}
          onStatus={onStatus}
          onApplyToAll={onApplyToAll}
        />
      ))}
    </div>
  );
}

function WearGroup({ group, itemsState, savingItems, disabled, onStatus, onApplyToAll }) {
  const [applying, setApplying] = useState(null); // 'new' | '25' | '50' | '75'
  const bulk = async (token) => {
    if (!onApplyToAll) return;
    setApplying(token);
    try { await onApplyToAll(group.items, token); }
    finally { setApplying(null); }
  };

  // Worst-of + summary counters for the group header pill.
  const counts = { new: 0, 25: 0, 50: 0, 75: 0, none: 0 };
  group.items.forEach((it) => {
    const s = String(itemsState[it.id]?.status || "").toLowerCase();
    if (s === "new") counts.new += 1;
    else if (s === "25") counts[25] += 1;
    else if (s === "50") counts[50] += 1;
    else if (s === "75") counts[75] += 1;
    else counts.none += 1;
  });
  const worst = counts[75] ? 75 : counts[50] ? 50 : counts[25] ? 25 : (counts.new ? 0 : -1);

  return (
    <div className="panel-heavy p-4" data-testid={`wear-group-${group.category}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: "rgba(245,130,32,0.14)",
              border: "1px solid rgba(245,130,32,0.35)",
              color: "var(--primary)",
            }}
          >
            <span className="font-display font-black text-xs">{group.short}</span>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] font-black" style={{ color: "var(--primary)" }}>
              {group.title}
            </div>
            <div className="text-[10px] text-muted mt-0.5">
              {group.items.length} position{group.items.length === 1 ? "" : "s"}
              {counts.none === 0 && <> · <span className="text-green-400 font-black">all answered</span></>}
              {counts.none > 0 && <> · <span className="text-muted">{counts.none} open</span></>}
              {worst >= 0 && (
                <> · worst <WearBadge pct={worst} compact /></>
              )}
            </div>
          </div>
        </div>

        {/* Set-all strip */}
        {onApplyToAll && !disabled && (
          <div className="flex items-center gap-1" data-testid={`wear-group-set-all-${group.category}`}>
            <span className="text-[9px] uppercase tracking-widest text-muted font-black mr-1">Set all</span>
            {WEAR_TOKENS.map((tok) => (
              <button
                key={tok.key}
                type="button"
                onClick={() => bulk(tok.key)}
                disabled={disabled || applying !== null}
                className="rounded-full text-[10px] font-black uppercase tracking-widest px-2 py-1 transition"
                style={{
                  background: applying === tok.key ? tok.bg : "var(--surface-2)",
                  border: `1px solid ${applying === tok.key ? tok.border : "var(--border)"}`,
                  color: applying === tok.key ? tok.color : "#a1a1aa",
                }}
                data-testid={`wear-group-${group.category}-apply-${tok.key}`}
                title={`Set every position in this group to ${tok.short}`}
              >
                {applying === tok.key ? <Loader2 className="w-2.5 h-2.5 animate-spin inline mr-1" /> : null}
                {tok.short}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Grid of position tiles */}
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {group.items.map((it) => (
          <PositionTile
            key={it.id}
            item={it}
            state={itemsState[it.id] || {}}
            saving={savingItems?.has(it.id)}
            disabled={disabled}
            onStatus={(tok) => onStatus(it, tok)}
          />
        ))}
      </div>
    </div>
  );
}

function PositionTile({ item, state, saving, disabled, onStatus }) {
  const [open, setOpen] = useState(false);
  const status = state?.status;
  const isNew = String(status || "").toLowerCase() === "new" || status === 0 || status === "0";
  const pct = isNew ? 0 : (status != null && status !== "" && !isNaN(Number(status)) ? Number(status) : null);
  const tone = pct == null ? { bg: "var(--surface-2)", border: "var(--border)", color: "#a1a1aa" } : wearTone(pct);
  const answered = pct != null || isNew;
  const shortLabel = shortenPositionLabel(item.label);

  // Cycle through tokens on tap: unanswered → NEW → 25 → 50 → 75 → back to NEW.
  const nextToken = () => {
    if (!answered) return "new";
    if (isNew) return "25";
    if (pct === 25) return "50";
    if (pct === 50) return "75";
    return "new";
  };

  return (
    <div
      className="panel-flat p-2 transition-transform hover:-translate-y-0.5"
      style={{ background: tone.bg, border: `1px solid ${tone.border}` }}
      data-testid={`wear-tile-${item.id}`}
    >
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="text-[10px] uppercase tracking-widest font-black text-white/85 truncate flex-1" title={item.label}>
          {shortLabel}
        </div>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-muted shrink-0" />}
        {answered && !saving && <CheckCircle2 className="w-3 h-3 text-green-400 shrink-0" />}
      </div>

      {/* Big status button — one tap cycles wear */}
      <button
        type="button"
        onClick={() => { if (disabled) return; if (open) setOpen(false); else onStatus(nextToken()); }}
        onContextMenu={(e) => { e.preventDefault(); if (!disabled) setOpen((v) => !v); }}
        disabled={disabled}
        className="w-full rounded-lg py-2 px-2 text-center transition"
        style={{
          background: tone.bg,
          border: `1px solid ${tone.border}`,
          color: tone.color,
        }}
        data-testid={`wear-tile-status-${item.id}`}
        title="Tap to cycle · long-tap for picker"
      >
        <div className="font-display font-black text-lg leading-none tabular-nums">
          {isNew ? "NEW" : pct != null ? `${pct}%` : "—"}
        </div>
        <div className="text-[9px] uppercase tracking-widest mt-0.5 flex items-center gap-1 justify-center opacity-80">
          {pct == null && !isNew ? "TAP" : (pct >= 75 ? "REPLACE" : pct >= 50 ? "MONITOR" : pct >= 25 ? "OK" : "NEW")}
          <ChevronDown className="w-2.5 h-2.5" onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} />
        </div>
      </button>

      {/* Inline picker — shown after long-press / chevron */}
      {open && !disabled && (
        <div className="grid grid-cols-4 gap-1 mt-2" data-testid={`wear-tile-picker-${item.id}`}>
          {WEAR_TOKENS.map((tok) => {
            const on = (tok.key === "new" && isNew) || (tok.pct === pct);
            return (
              <button
                key={tok.key}
                type="button"
                onClick={() => { onStatus(tok.key); setOpen(false); }}
                className="rounded-md py-1 px-1 text-[9px] font-black uppercase tracking-widest transition"
                style={{
                  background: on ? tok.bg : "rgba(255,255,255,0.04)",
                  border: `1px solid ${on ? tok.border : "var(--border)"}`,
                  color: on ? tok.color : "#a1a1aa",
                }}
                data-testid={`wear-tile-picker-${item.id}-${tok.key}`}
              >
                {tok.short}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function WearBadge({ pct, compact = false }) {
  const isNew = pct === 0;
  const tone = isNew
    ? { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" }
    : wearTone(pct);
  return (
    <span
      className="inline-flex items-center rounded-md font-black uppercase tracking-widest tabular-nums"
      style={{
        background: tone.bg,
        border: `1px solid ${tone.border}`,
        color: tone.color,
        padding: compact ? "0 4px" : "2px 6px",
        fontSize: compact ? 9 : 10,
      }}
    >
      {isNew ? "NEW" : `${pct}%`}
    </span>
  );
}

/* ------------ helpers ------------ */

const WEAR_TOKENS = [
  { key: "new", pct: 0,  short: "NEW", bg: "#0a2a12", border: "#4ade80", color: "#4ade80" },
  { key: "25",  pct: 25, short: "25%", bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" },
  { key: "50",  pct: 50, short: "50%", bg: "#3a2f0d", border: "#c9a227", color: "#facc15" },
  { key: "75",  pct: 75, short: "75%", bg: "#4a2a06", border: "#f58220", color: "#ffb347" },
];

function wearTone(pct) {
  if (pct >= 100) return { bg: "#5a0f0f", border: "#a51919", color: "#ff9b9b" };
  if (pct >= 75)  return { bg: "#4a2a06", border: "#f58220", color: "#ffb347" };
  if (pct >= 50)  return { bg: "#3a2f0d", border: "#c9a227", color: "#facc15" };
  if (pct >= 25)  return { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" };
  return              { bg: "#0a2a12", border: "#1e6b32", color: "#4ade80" };
}

// Category detection mirrors the app's Tooth / Lip Shroud / Wing Shroud
// grouping. Anything not matching stays in the "other" bucket (rendered as a
// single group at the bottom).
function categoryOf(label) {
  const L = String(label || "").toLowerCase();
  if (L.includes("wing")) return { key: "wing_shroud", title: "Wing Shrouds", short: "WS", order: 3 };
  if (L.includes("lip"))  return { key: "lip_shroud",  title: "Lip Shrouds",  short: "LS", order: 2 };
  if (L.includes("tooth")) return { key: "tooth",      title: "Teeth",        short: "T",  order: 1 };
  return { key: "other", title: "Other Wear", short: "?", order: 9 };
}

function groupByCategory(items) {
  const map = new Map();
  items.forEach((it) => {
    const cat = categoryOf(it.label);
    if (!map.has(cat.key)) map.set(cat.key, { category: cat.key, title: cat.title, short: cat.short, order: cat.order, items: [] });
    map.get(cat.key).items.push(it);
  });
  const arr = Array.from(map.values());
  arr.sort((a, b) => a.order - b.order);
  arr.forEach((g) => g.items.sort((a, b) => {
    const na = extractPos(a.label);
    const nb = extractPos(b.label);
    if (na != null && nb != null) return na - nb;
    return String(a.label).localeCompare(String(b.label));
  }));
  return arr;
}

function extractPos(label) {
  const m = String(label).match(/(\d+)/);
  return m ? Number(m[1]) : null;
}

function shortenPositionLabel(label) {
  // "Tooth Pos 3" → "T3", "Wing Shroud Pos 5" → "WS5", "Lip Shroud Pos 2" → "LS2"
  const L = String(label || "");
  const pos = extractPos(L);
  if (pos == null) return L;
  const l = L.toLowerCase();
  if (l.includes("wing"))  return `Pos ${pos}`;
  if (l.includes("lip"))   return `Pos ${pos}`;
  if (l.includes("tooth")) return `Pos ${pos}`;
  return L;
}
